import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Search, Edit2, Trash2, Eye, CheckCircle2, Folder, Loader2 } from "lucide-react";

const API_BASE = "/api";

interface Project {
  id: number;
  title: string;
  category: string;
  location: string;
  year: string;
  client: string;
  image_url: string;
  gallery_images?: string[];
  status: string;
  description?: string;
  services?: string[];
}

const SAMPLE_PROJECTS: Project[] = [
  {
    id: 1,
    title: "Eko Atlantic City Development",
    category: "Topographic Survey",
    location: "Eko Atlantic, Lagos",
    year: "2023",
    client: "Eko Atlantic Development Company",
    image_url: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800",
    status: "Completed",
    description: "Comprehensive topographic survey for the prestigious Eko Atlantic City development project.",
    services: ["Topographic Survey", "Boundary Survey", "Construction Staking"],
  },
  {
    id: 2,
    title: "Lekki Free Trade Zone",
    category: "Boundary Survey",
    location: "Ibeju-Lekki, Lagos",
    year: "2023",
    client: "Lekki Free Zone Development Company",
    image_url: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800",
    status: "Completed",
    description: "Detailed boundary demarcation and cadastral survey.",
    services: ["Boundary Survey", "Cadastral Survey", "GIS Mapping"],
  },
  {
    id: 3,
    title: "Ibadan Expressway Construction",
    category: "Construction Survey",
    location: "Lagos-Ibadan Expressway",
    year: "2022",
    client: "Julius Berger Nigeria",
    image_url: "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800",
    status: "Completed",
    description: "Precision construction staking and alignment survey.",
    services: ["Construction Survey", "Route Survey"],
  },
];

export default function AdminProjects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/projects`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` } });
      const data = await response.json();

      if (!response.ok || data.error) {
        throw new Error(data.error || "Failed to fetch projects");
      }

      if (!Array.isArray(data)) {
        throw new Error("Invalid response format");
      }

      if (data.length === 0) {
        console.log('No projects from API, using sample data');
        setProjects(SAMPLE_PROJECTS);
        setError("Using demo data. Connect D1 database to persist changes.");
      } else {
        const parsed = data.map((p: Project) => ({
          ...p,
          services:
            typeof p.services === "string" ? JSON.parse(p.services) : p.services || [],
        }));
        setProjects(parsed);
      }
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setProjects(SAMPLE_PROJECTS);
      setError("Using demo data. API unavailable - connect D1 database to persist changes.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await fetch(`${API_BASE}/projects/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken') || ''}` },
      });
      if (!response.ok) throw new Error("Failed to delete project");
      setProjects(projects.filter((p) => p.id !== id));
      setShowDeleteConfirm(null);
    } catch (err) {
      alert("Error deleting project: " + (err instanceof Error ? err.message : "Unknown error"));
    }
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="w-8 h-0.5 bg-accent rounded-full" />
            <span className="text-accent text-xs font-medium uppercase tracking-wide">Projects</span>
          </div>
          <h1 className="text-2xl font-bold text-primary">Manage Projects</h1>
          <p className="text-neutral-400 text-sm mt-1">Add, edit, or remove portfolio projects</p>
        </div>
        <button
          onClick={() => navigate("/admin/projects/new")}
          className="bg-accent hover:bg-accent/90 text-white px-3 sm:px-4 py-2 rounded-xl flex items-center justify-center gap-2 transition-all shadow-lg shadow-accent/20 font-medium text-sm"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">Add Project</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-red-600 text-sm">
          <div className="font-semibold mb-1">Error loading projects</div>
          <div>{error}</div>
          <div className="mt-2 text-xs text-red-500">
            Please check that the database is connected and try refreshing the page.
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-white rounded-xl border border-neutral-100 p-5">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-neutral-200 focus:border-accent focus:ring-2 focus:ring-accent/20 outline-none text-sm"
          />
        </div>
      </div>

      {/* Projects Table */}
      <div className="bg-white rounded-xl border border-neutral-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-accent animate-spin" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <table className="w-full min-w-[640px]">
                <thead>
                  <tr className="border-b border-neutral-100 bg-neutral-50/50">
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-neutral-600 text-xs sm:text-sm">
                      Project
                    </th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-neutral-600 text-xs sm:text-sm">
                      Category
                    </th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-neutral-600 text-xs sm:text-sm hidden md:table-cell">
                      Client
                    </th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-neutral-600 text-xs sm:text-sm">
                      Year
                    </th>
                    <th className="text-left py-2 sm:py-3 px-2 sm:px-4 font-medium text-neutral-600 text-xs sm:text-sm">
                      Status
                    </th>
                    <th className="text-right py-2 sm:py-3 px-2 sm:px-4 font-medium text-neutral-600 text-xs sm:text-sm">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map((project) => (
                    <tr
                      key={project.id}
                      className="border-b border-neutral-50 hover:bg-neutral-50/50 transition-colors"
                    >
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <img
                            src={project.image_url || "https://via.placeholder.com/40"}
                            alt={project.title}
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover flex-shrink-0"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = "https://via.placeholder.com/40";
                            }}
                          />
                          <div className="min-w-0">
                            <div className="font-medium text-primary text-xs sm:text-sm truncate">
                              {project.title}
                            </div>
                            <div className="text-[10px] sm:text-xs text-neutral-400 truncate">
                              {project.location}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-neutral-500 text-xs sm:text-sm">
                        <span className="hidden sm:inline">{project.category}</span>
                        <span className="sm:hidden">{project.category?.split(" ")[0]}</span>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-neutral-500 text-xs sm:text-sm hidden md:table-cell">
                        {project.client}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4 text-neutral-500 text-xs sm:text-sm">
                        {project.year}
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <span
                          className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium ${
                            project.status === "Completed"
                              ? "bg-green-500/10 text-green-600"
                              : project.status === "In Progress"
                              ? "bg-blue-500/10 text-blue-600"
                              : "bg-yellow-500/10 text-yellow-600"
                          }`}
                        >
                          {project.status}
                        </span>
                      </td>
                      <td className="py-2 sm:py-3 px-2 sm:px-4">
                        <div className="flex items-center justify-end gap-0.5 sm:gap-1">
                          <a
                            href={`/projects/${project.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1 sm:p-1.5 hover:bg-neutral-100 rounded-lg text-neutral-400 hover:text-primary transition-colors"
                            title="View"
                          >
                            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </a>
                          <button
                            onClick={() => navigate(`/admin/projects/edit/${project.id}`)}
                            className="p-1 sm:p-1.5 hover:bg-neutral-100 rounded-lg text-neutral-400 hover:text-accent transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                          </button>
                          <button
                            onClick={() => setShowDeleteConfirm(project.id)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-neutral-400 hover:text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredProjects.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="w-14 h-14 bg-neutral-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Folder className="w-7 h-7 text-neutral-300" />
                </div>
                <h3 className="text-base font-medium text-neutral-600 mb-1">No projects found</h3>
                <p className="text-neutral-400 text-sm">Add a new project to get started</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-3 sm:p-4"
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              className="bg-white rounded-2xl p-5 sm:p-6 max-w-sm w-full shadow-2xl"
            >
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-primary text-center mb-2">
                Delete Project?
              </h3>
              <p className="text-neutral-500 text-sm text-center mb-5 sm:mb-6">
                This action cannot be undone. The project will be permanently removed.
              </p>
              <div className="flex gap-2 sm:gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 px-3 sm:px-4 py-2 rounded-xl border border-neutral-200 text-neutral-600 hover:bg-neutral-50 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDelete(showDeleteConfirm)}
                  className="flex-1 px-3 sm:px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white transition-colors text-sm font-medium"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
